import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useShowAlert } from 'hooks';
import API from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import { FormFieldsMap } from 'UI/components/organisms/CompanyForm/fields';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';
import { preloadFromBackend, prepareFormToSubmit } from 'UI/utils/forms';

import { formatPhoneNumbers, PHONE_FORMAT_MODES } from '../PhoneNumbersForm/utils';

/**
 * @param {Object} props
 * @param {*} props.company
 * @param {Function} props.onBeforeSubmit
 * @param {Function} props.onEditCompleted
 * @param {Function} props.onEditClosed
 * @param {*} props.CompanyForm
 */
const CompanyEdit = ({ company, onEditCompleted, onEditClosed, onBeforeSubmit, CompanyForm }) => {
  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isFormDisabled: false,
    isReadOnly: false
  });

  const { showAlert } = useShowAlert();

  const defaultValues = preloadFromBackend(company, FormFieldsMap);

  const form = useForm(
    company
      ? {
          defaultValues
        }
      : {}
  );

  const { handleSubmit } = form;

  const onSubmit = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      const transformedData = prepareFormToSubmit(formData, FormFieldsMap);
      const transformedPhones = formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit);
      const companyData = {
        ...transformedData,
        phones: transformedPhones
      };
      onBeforeSubmit && onBeforeSubmit(companyData);

      const response = await API.put(`${Endpoints.Companies}/${company.id}`, companyData, {
        apiVersion: 2
      });

      if (response.data && response.status === 201) {
        showAlert({
          severity: 'success',
          title: strings.formatString(strings.inventoryProfiles.common.edition, {
            inventoryItem: 'Company'
          })
        });

        const updatedProfile = response.data;
        onEditCompleted && onEditCompleted(updatedProfile);
      }
    } catch (err) {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false,
        isSuccess: false
      }));
      showAlert({
        severity: 'error',
        title: 'Company',
        body: getErrorMessage(err)
      });
    }
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose: onEditClosed,
    isSaveDisabled: uiState.isSaving,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{
        title: strings.inventoryProfiles.common.editProfile,
        subheader: company?.name
      }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
      onClose={onEditClosed}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>{CompanyForm}</FormContext>
    </FPDrawer>
  );
};

export default CompanyEdit;
